import * as Sentry from '@sentry/react';

/**
 * Inicializa Sentry en el frontend. No-op si no hay DSN.
 * En Fase 0 (auth) añadiremos company_id/user_id al scope.
 */
/**
 * Errores de "chunk viejo" tras un deploy: transitorios y auto-recuperables (el usuario recarga
 * y toma el bundle nuevo). Son ruido en Sentry — se descartan. Se listan solo patrones INEQUÍVOCOS
 * de carga de módulo dinámico (no filtramos TypeErrors genéricos, que sí pueden ser bugs reales).
 */
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  "'text/html' is not a valid javascript mime type",
  'chunkloaderror',
  'loading chunk',
  'loading css chunk',
];

function isTransientChunkError(msg: string): boolean {
  const m = msg.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((p) => m.includes(p));
}

/**
 * Fallo de RED del navegador (no del código): el backend reiniciándose en un deploy o el móvil
 * sin señal. `fetch` rechaza siempre con el mismo texto, sin importar el motivo.
 */
const NETWORK_ERROR_PATTERNS = [
  'failed to fetch',            // Chrome / Edge
  'networkerror when attempting to fetch', // Firefox
  'load failed',                // Safari
  'network request failed',
  'the internet connection appears to be offline',
];

function isNetworkError(msg: string): boolean {
  const m = msg.toLowerCase();
  // Un chunk error también dice "failed to fetch dynamically imported module": ese ya se filtra antes.
  return NETWORK_ERROR_PATTERNS.some((p) => m.includes(p));
}

/**
 * Nuestro PROPIO error de red (`ApiError` con code `network_error`, ver lib/api.ts). El texto ya
 * viene traducido y legible ("The connection dropped while sending…"), o sea que no matchea
 * ninguno de los patrones crudos de arriba: entraba a Sentry como error de código y ensuciaba la
 * lista con lo mismo que ya sabemos filtrar cuando `fetch` revienta en crudo.
 */
/**
 * SESIÓN VENCIDA: ya está resuelta antes de llegar acá.
 *
 * Cuando el backend contesta 401, `lib/api.ts` guarda dónde estaba la persona y la manda al
 * login — que es exactamente lo que hay que hacer. Pero el `throw` sigue viajando, y de las 91
 * pantallas que llaman a su `load()` dentro de un useEffect ninguna lo ataja, así que sale como
 * `unhandledrejection` y aterriza en Sentry con cara de bug (2026-09-17, en /pay-queue).
 *
 * Que una sesión venza no es un error del código. La marca la pone `api.ts` SOLO cuando ya
 * redirigió, así que un 401 que no llegó a resolverse sigue llegando.
 */
export function esSesionVencida(err: unknown): boolean {
  return typeof err === 'object' && err !== null
    && (err as { sessionExpired?: unknown }).sessionExpired === true;
}

function isOurNetworkApiError(err: unknown): boolean {
  return typeof err === 'object' && err !== null
    && (err as { code?: unknown }).code === 'network_error'
    && (err as { status?: unknown }).status === 0;
}

/**
 * RUIDO DE EXTENSIONES DEL NAVEGADOR — NUNCA ES UN BUG NUESTRO.
 *
 * Sentry 2026-09-16, en /p/update/… (la página pública que abre el homeowner desde un SMS):
 *   `Error: Invalid call to runtime.sendMessage(). Tab not found.`
 *
 * `runtime.sendMessage` es la API de EXTENSIONES de Chrome. Nuestro código no la llama en ningún
 * lado — la inyecta una extensión instalada en el navegador de quien abrió el link, y al reventar
 * queda enganchada en nuestro handler global de errores. No se puede arreglar desde acá: ni el
 * código es nuestro ni sabemos qué extensión es.
 *
 * Se filtra por DOS caminos, porque uno solo se escapa: el texto conocido, y cualquier marco de la
 * pila que venga de un `chrome-extension://` (o su equivalente en Firefox y Safari) — eso último
 * caza también las extensiones que todavía no sabemos nombrar.
 */
const TEXTOS_DE_EXTENSION = [
  'runtime.sendmessage',
  'extension context invalidated',
  'chrome-extension://',
  'moz-extension://',
  'safari-web-extension://',
  'message port closed before a response was received',
  // Outlook / Office "Safe Links" corriendo en el navegador del visitante (Sentry 2026-10-09, formulario público de
  // intake, Chrome en Windows): "Object Not Found Matching Id:2, MethodName:update, ParamCount:4". No es nuestro código.
  'object not found matching id',
];

export function esRuidoDeExtension(msg: string, frames?: Array<{ filename?: string }>): boolean {
  const m = (msg || '').toLowerCase();
  if (TEXTOS_DE_EXTENSION.some((t) => m.includes(t))) return true;
  return (frames ?? []).some((f) => /^(chrome|moz|safari-web)-extension:\/\//i.test(f?.filename ?? ''));
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN_FRONTEND;
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    beforeSend(event, hint) {
      const err = hint?.originalException;
      const msg = err instanceof Error ? `${err.name}: ${err.message}` : (event.exception?.values?.[0]?.value ?? '');
      if (isTransientChunkError(msg)) return null; // no reportar chunk viejo (auto-recuperable)
      // Código que no es nuestro y que no podemos arreglar: se descarta entero.
      if (esRuidoDeExtension(msg, event.exception?.values?.[0]?.stacktrace?.frames)) return null;
      if (esSesionVencida(err)) return null; // ya se mandó a la persona al login

      if (isNetworkError(msg) || isOurNetworkApiError(err)) {
        // Usuario sin internet → 100% ruido, se descarta. Con internet puede ser el backend
        // reiniciándose (deploy) o caído de verdad: se conserva como WARNING con tag para poder
        // filtrarlo, en vez de ensuciar la lista de errores de código.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;
        event.level = 'warning';
        event.tags = { ...(event.tags ?? {}), transient: 'network' };
        event.fingerprint = ['network-unreachable']; // todos agrupados en UN issue, no uno por stack
      }
      return event;
    },
  });
}
